<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link
        view="AdminAlertPayerList"
        label="Alert Payers"
        :active="true"
      />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div>
              <h1 class="title mb-2">Manage Alert Payers</h1>
              <p class="subtitle is-6 has-text-grey mt-0">Total: {{ totalCount }} payers</p>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-button type="is-success" @click="openCreateModal">
              <b-icon icon="plus"></b-icon>
              <span>Create New</span>
            </b-button>
          </div>
        </div>
      </div>
      <hr />

      <!-- Group assignment -->
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <b-field grouped label="Payer Group" v-if="payerGroupOptions.length > 0 || checkedRows.length > 0">
              <div class="group-select-wrapper">
                <b-select
                  :key="groupSelectKey"
                  v-model="payerGroupSelected"
                  placeholder="Change group for selected"
                  :loading="loading"
                  :disabled="!checkedRows.length || !payerGroupOptions.length"
                  expanded
                  @input="handlePayerGroupBulkAdd($event)"
                >
                  <option v-for="option in payerGroupOptions" :value="option.id" :key="option.id">
                    {{ option.name }}
                  </option>
                </b-select>
              </div>
            </b-field>
          </div>
        </div>
      </div>

      <!-- Search -->
      <b-field>
        <div class="field-body">
          <b-field label="Search payers">
            <b-input
              v-model="search"
              placeholder="Search by name..."
              icon="magnify"
              @input="debouncedSearch"
            ></b-input>
          </b-field>
        </div>
      </b-field>

      <div class="table-container">
        <b-table
          :data="payers"
          :loading="loading"
          hoverable
          striped
          :paginated="true"
          backend-pagination
          :total="totalCount"
          :per-page="perPage"
          :current-page="currentPage"
          @page-change="onPageChange"
          pagination-position="bottom"
          default-sort="name"
          default-sort-direction="asc"
          :checked-rows.sync="checkedRows"
          checkable
        >
          <b-table-column field="name" label="Name" v-slot="props" sortable>
            <span class="has-text-weight-semibold">{{ props.row.name }}</span>
          </b-table-column>

          <b-table-column field="payer_group" label="Group" v-slot="props">
            <span v-if="props.row.is_group">
              <b-icon type="is-success" icon="check-circle" size="is-small" />
            </span>
            <b-tag
              v-else-if="props.row.payer_group"
              type="is-info"
              closable
              aria-close-label="Remove from group"
              @close="handlePayerGroupRemove(props.row.id)"
            >
              {{ props.row.payer_group.name }}
            </b-tag>
          </b-table-column>

          <b-table-column label="Actions" width="160" v-slot="props">
            <div class="field is-grouped">
              <div class="control">
                <b-button
                  type="is-primary is-outlined"
                  size="is-small"
                  icon-left="pencil"
                  @click="openEditModal(props.row)"
                >
                  Edit
                </b-button>
              </div>
              <div class="control">
                <b-button
                  type="is-danger"
                  size="is-small"
                  icon-left="delete"
                  @click="confirmDelete(props.row)"
                >
                  Delete
                </b-button>
              </div>
            </div>
          </b-table-column>

          <template #empty>
            <div class="has-text-centered">
              <b-icon icon="emoticon-sad" size="is-large"></b-icon>
              <p>No payers found.</p>
            </div>
          </template>
        </b-table>
      </div>

      <!-- Create Modal -->
      <b-modal v-model="isCreateModalActive" :width="500" scroll="keep">
        <form @submit.prevent="createPayer">
          <div class="modal-card">
            <header class="modal-card-head">
              <p class="modal-card-title">Create New Payer</p>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="isCreating"></b-loading>

              <b-field
                label="Name"
                required
                :type="{ 'is-danger': !!createErrors.name }"
                :message="createErrors.name"
              >
                <b-input
                  v-model="createForm.name"
                  placeholder="Enter payer name"
                  required
                  maxlength="255"
                ></b-input>
              </b-field>

              <div class="field">
                <label class="label">Mark as Group</label>
                <b-switch v-model="createForm.is_group" type="is-success">
                  {{ createForm.is_group ? 'Yes — this payer is a group parent' : 'No' }}
                </b-switch>
              </div>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="closeCreateModal">Cancel</b-button>
              <b-button type="is-success" native-type="submit" :loading="isCreating">
                Create
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>

      <!-- Edit Modal -->
      <b-modal v-model="showEditModal" :width="500" scroll="keep">
        <form @submit.prevent="handleUpdatePayer">
          <div class="modal-card" style="width: auto">
            <header class="modal-card-head">
              <p class="modal-card-title">Edit Payer</p>
              <button type="button" class="delete" @click="showEditModal = false"></button>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="editLoading"></b-loading>

              <b-field label="Name" :type="{ 'is-danger': !!errors.name }" :message="errors.name">
                <b-input
                  v-model="editForm.name"
                  placeholder="Enter payer name"
                  required
                  maxlength="255"
                ></b-input>
              </b-field>

              <div class="field">
                <label class="label">Mark as Group</label>
                <b-switch v-model="editForm.is_group" type="is-success">
                  {{ editForm.is_group ? 'Yes — this payer is a group parent' : 'No' }}
                </b-switch>
              </div>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="showEditModal = false">Cancel</b-button>
              <b-button type="is-primary" native-type="submit" :loading="editLoading">
                Save Changes
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>
    </div>
  </AdminLayout>
</template>

<script>
import request from '../../request';
import AdminLayout from '../../layouts/AdminLayout.vue';
export default {
  name: 'AdminAlertPayerList',
  components: {
    AdminLayout,
  },
  data() {
    return {
      loading: false,
      payers: [],
      payerGroupOptions: [],
      checkedRows: [],
      payerGroupSelected: null,
      groupSelectKey: 0,
      totalCount: 0,
      currentPage: 1,
      perPage: 50,
      search: '',
      searchTimeout: null,
      isCreateModalActive: false,
      createForm: {
        name: '',
        is_group: false,
      },
      createErrors: {},
      isCreating: false,
      showEditModal: false,
      editLoading: false,
      editingPayer: null,
      editForm: {},
      errors: {},
    };
  },
  created() {
    this.loadPayers();
    this.loadPayerGroupOptions();
  },
  methods: {
    async loadPayerGroupOptions() {
      try {
        const response = await request.get('/admin/alerts/payers?is_group=true&limit=500');
        this.payerGroupOptions = (response.data.payers || []).filter((p) => p.is_group);
      } catch (error) {
        console.error('Error loading payer group options:', error);
      }
    },
    async loadPayers() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        if (this.perPage) params.append('limit', this.perPage);
        if (this.currentPage > 1) params.append('page', this.currentPage);
        if (this.search.trim()) params.append('term', this.search.trim());
        const response = await request.get(`/admin/alerts/payers?${params.toString()}`);
        this.payers = response.data.payers || [];
        this.totalCount = parseInt(response.headers['x-total-count'] || '0');
      } catch (error) {
        console.error('Error loading payers:', error);
        this.$buefy.toast.open({
          message: 'Failed to load payers',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadPayers();
    },
    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadPayers();
      }, 300);
    },
    handlePayerGroupBulkAdd(payerGroupId) {
      // Reset and force re-render so selecting the same option always re-triggers @input,
      // including when there is only one option in the list.
      this.payerGroupSelected = null;
      this.groupSelectKey++;

      const payerGroup = this.payerGroupOptions.find((p) => p.id === payerGroupId);
      if (!payerGroup) return;

      // Validate: group parents cannot be added as children
      const groupParents = this.checkedRows.filter((r) => r.is_group);
      if (groupParents.length > 0) {
        this.$buefy.dialog.alert({
          title: 'Invalid Selection',
          message: `The following payers are group parents and cannot be added to another group:<br/><strong>${groupParents.map((r) => r.name).join(', ')}</strong><br/><br/>Please uncheck them and try again.`,
          type: 'is-danger',
          hasIcon: true,
        });
        return;
      }

      // Validate: payers already in a group cannot be moved to another group
      const alreadyInGroup = this.checkedRows.filter((r) => r.payer_group_id && r.payer_group_id !== payerGroupId);
      if (alreadyInGroup.length > 0) {
        this.$buefy.dialog.alert({
          title: 'Invalid Selection',
          message: `The following payers already belong to a group:<br/><strong>${alreadyInGroup.map((r) => r.name).join(', ')}</strong><br/><br/>Remove them from their current group first.`,
          type: 'is-danger',
          hasIcon: true,
        });
        return;
      }

      const message = `Are you sure you want to add ${this.checkedRows.length} payer(s) to the group "<strong>${payerGroup.name}</strong>"?`;

      this.$buefy.dialog.confirm({
        message,
        confirmText: `Add to ${payerGroup.name}`,
        type: 'is-success',
        onConfirm: () => {
          this.loading = true;
          // Filter defensively at confirm time so unchecked rows are never sent,
          // even if the table's .sync binding had a stale reference earlier.
          const ids = this.checkedRows
            .filter((r) => !r.is_group && !r.payer_group_id)
            .map((r) => r.id);
          request
            .put(`/admin/alerts/payers/${payerGroupId}/bulk`, { payer_ids: ids })
            .then(() => {
              this.loadPayers();
              this.loadPayerGroupOptions();
            })
            .catch(() => {
              this.$buefy.toast.open({ message: 'Failed to update group', type: 'is-danger' });
            })
            .finally(() => {
              this.checkedRows = [];
              this.loading = false;
            });
        },
      });
    },
    handlePayerGroupRemove(payerId) {
      const payer = this.payers.find((p) => p.id === payerId);
      this.$buefy.dialog.confirm({
        message: `Are you sure you want to remove "<strong>${payer.name}</strong>" from the group "<strong>${payer.payer_group.name}</strong>"?`,
        confirmText: 'Remove From Group',
        type: 'is-danger',
        onConfirm: () => {
          this.loading = true;
          request
            .put(`/admin/alerts/payers/${payerId}`, { payer_group_id: 0 })
            .then(() => {
              this.loadPayers();
            })
            .catch(() => {
              this.$buefy.toast.open({ message: 'Failed to remove from group', type: 'is-danger' });
            })
            .finally(() => (this.loading = false));
        },
      });
    },
    openCreateModal() {
      this.createForm = {
        name: '',
        is_group: false,
      };
      this.createErrors = {};
      this.isCreateModalActive = true;
    },
    closeCreateModal() {
      this.isCreateModalActive = false;
      this.createForm = {
        name: '',
        is_group: false,
      };
      this.createErrors = {};
    },
    async createPayer() {
      this.isCreating = true;
      this.createErrors = {};
      try {
        const payload = {
          name: this.createForm.name?.trim(),
          is_group: this.createForm.is_group,
        };
        await request.post('/admin/alerts/payers', payload);
        this.$buefy.toast.open({
          message: 'Payer created successfully',
          type: 'is-success',
        });
        this.closeCreateModal();
        this.loadPayers();
        this.loadPayerGroupOptions();
      } catch (error) {
        console.error('Error creating payer:', error);
        if (error.response?.data?.errors) {
          this.createErrors = error.response.data.errors;
          return;
        }
        const msg = error.response?.data?.code == 422 ? 'Payer with this name already exists' : 'Failed to create payer';
        this.$buefy.toast.open({
          message: msg,
          type: 'is-danger',
        });
      } finally {
        this.isCreating = false;
      }
    },
    openEditModal(payer) {
      this.editingPayer = payer;
      this.editForm = {
        name: payer.name,
        is_group: payer.is_group,
      };
      this.errors = {};
      this.showEditModal = true;
    },
    async handleUpdatePayer() {
      this.editLoading = true;
      this.errors = {};

      // A payer that is already a child of a group cannot be marked as a group parent
      if (this.editForm.is_group && this.editingPayer.payer_group_id) {
        this.$buefy.toast.open({
          message: 'This payer is already a child of a group. Remove it from its group before marking it as a group parent.',
          type: 'is-danger',
          duration: 5000,
        });
        this.editLoading = false;
        return;
      }

      try {
        const payload = {
          name: this.editForm.name?.trim(),
          is_group: this.editForm.is_group,
        };
        await request.put(`/admin/alerts/payers/${this.editingPayer.id}`, payload);
        this.$buefy.toast.open({
          message: 'Payer updated successfully',
          type: 'is-success',
        });
        this.showEditModal = false;
        this.loadPayers();
        this.loadPayerGroupOptions();
      } catch (error) {
        console.error('Error updating payer:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else if (error.response?.data?.message) {
          let msg = error.response?.data?.code == 422 ? 'Payer with this name already exists' : 'Failed to update payer';
          this.$buefy.toast.open({
            message: msg,
            type: 'is-danger',
          });
        } else {
          this.$buefy.toast.open({
            message: 'Failed to update payer',
            type: 'is-danger',
          });
        }
      } finally {
        this.editLoading = false;
      }
    },
    confirmDelete(payer) {
      this.$buefy.dialog.confirm({
        title: 'Delete Payer',
        message: `Are you sure you want to delete "<strong>${payer.name}</strong>"? This action cannot be undone.`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deletePayer(payer),
      });
    },
    async deletePayer(payer) {
      try {
        await request.delete(`/admin/alerts/payers/${payer.id}`);
        this.$buefy.toast.open({
          message: 'Payer deleted successfully',
          type: 'is-success',
        });
        this.loadPayers();
      } catch (error) {
        console.error('Error deleting payer:', error);
        let errorMessage = 'Failed to delete payer';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.status === 400) {
          errorMessage = 'Cannot delete this payer - it may still be in use by alerts';
        }
        this.$buefy.dialog.alert({
          title: 'Delete Failed',
          message: errorMessage,
          type: 'is-danger',
          hasIcon: true,
          ariaRole: 'alertdialog',
          ariaModal: true,
        });
      }
    },
  },
};
</script>

<style scoped>
.group-select-wrapper {
  width: 260px;
}

.group-select-wrapper .select,
.group-select-wrapper select {
  width: 100%;
}
</style>
